"use client";

/**
 * Hook to close a menu/dropdown on outside click or Escape key press
 */

import { type RefObject, useEffect } from "react";

/**
 * Registers mousedown and keydown listeners when `isOpen` is true.
 * Calls `onClose` when clicking outside `ref` or pressing Escape.
 * @param ref - Ref to the element that defines the "inside" boundary
 * @param isOpen - Whether the menu/dropdown is currently open
 * @param onClose - Callback fired on outside click or Escape key (should be stable/memoized)
 */
export function useClickOutside(ref: RefObject<HTMLElement | null>, isOpen: boolean, onClose: () => void): void {
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && event.target instanceof Node && !ref.current.contains(event.target)) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [ref, isOpen, onClose]);
}
